
import React, { useState } from "react";
import { Eye, MapPin, Image, Target } from "lucide-react";
import { useChat } from "@/context/ChatContext";
import { useTokenWarnings } from "@/hooks/useTokenWarnings";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TargetButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  codeOverride?: string;
  disabled?: boolean;
  isGenerating?: boolean;
}

const TargetButton: React.FC<TargetButtonProps> = ({
  icon,
  label,
  onClick,
  codeOverride,
  disabled = false,
  isGenerating = false,
}) => {
  // Create the full button text including the code if available
  const buttonText = codeOverride ? `${label}: ${codeOverride}` : label;
  
  return (
    <Button
      variant="target"
      size="target"
      onClick={onClick}
      disabled={disabled || isGenerating}
      className={cn(
        "gap-1 whitespace-nowrap",
        (disabled || isGenerating) && "opacity-70 cursor-not-allowed"
      )}
    >
      <span className="mr-1">
        {isGenerating ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          icon
        )}
      </span>
      <span className="hidden sm:inline">{buttonText}</span>
      <span className="sm:hidden">{codeOverride ? `${label.split(' ')[0]}: ${codeOverride}` : label.split(' ')[0]}</span>
    </Button>
  );
};

interface TargetButtonsProps {
  onButtonClick: (buttonLabel: string) => void;
}

const TargetButtons: React.FC<TargetButtonsProps> = ({ onButtonClick }) => {
  const { currentTargetCode, isLoading, targetIsRevealed } = useChat();
  const { isInitialized } = useTokenWarnings();
  const [generatingType, setGeneratingType] = useState<string | null>(null);

  // Format the target code for display (add hyphen if it's 8 characters without one)
  const formatCodeForDisplay = (code: string | null): string | undefined => {
    if (!code) return undefined;
    
    // If it's 8 characters without a hyphen, format it as XXXX-XXXX for display
    if (code.length === 8 && !code.includes('-')) {
      return `${code.slice(0, 4)}-${code.slice(4)}`;
    }
    
    // If it's already hyphenated or different format, return as-is
    return code;
  };

  const displayCode = formatCodeForDisplay(currentTargetCode);

  const handleButtonClick = (buttonLabel: string, targetType: string) => {
    console.log(`🔘 TargetButton: ${targetType} button clicked`);
    setGeneratingType(targetType);
    onButtonClick(buttonLabel);
    
    // Reset generating state after a timeout as fallback
    setTimeout(() => {
      setGeneratingType(null);
    }, 30000); // 30 second timeout
  };

  const handleRevealTargetClick = () => {
    if (!currentTargetCode) {
      console.log('🔘 TargetButton: Reveal button clicked but no target code available');
      return;
    }
    
    const message = displayCode ? `Reveal target: ${displayCode}` : "Reveal target";
    onButtonClick(message);
  };

  // Reset generating state when not loading
  React.useEffect(() => {
    if (!isLoading && generatingType) {
      setGeneratingType(null);
    }
  }, [isLoading]);

  // Note: Removed blocking loading state to allow immediate rendering

  return (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 max-w-full">
      <TargetButton
        icon={<MapPin size={16} className="sm:w-5 sm:h-5" />}
        label="Location target"
        onClick={() => handleButtonClick("Location target", "location")}
        disabled={isLoading || !isInitialized}
        isGenerating={generatingType === "location"}
      />
      <TargetButton
        icon={<Image size={16} className="sm:w-5 sm:h-5" />}
        label="Image target"
        onClick={() => handleButtonClick("Image target", "image")}
        disabled={isLoading || !isInitialized}
        isGenerating={generatingType === "image"}
      />
      <TargetButton
        icon={<Target size={16} className="sm:w-5 sm:h-5" />}
        label="Prediction target"
        onClick={() => handleButtonClick("Prediction target", "prediction")}
        disabled={isLoading || !isInitialized}
        isGenerating={generatingType === "prediction"}
      />
      <TargetButton
        icon={<Eye size={16} className="sm:w-5 sm:h-5" />}
        label={targetIsRevealed ? "Target Revealed" : "Reveal target"}
        codeOverride={displayCode}
        onClick={targetIsRevealed ? undefined : handleRevealTargetClick}
        disabled={!currentTargetCode || !isInitialized || targetIsRevealed}
      />
    </div>
  );
};

export default TargetButtons;
